import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { IoArrowUpCircleOutline } from "react-icons/io5";
import EventTabs from "./EventTabs";
import { BASEAPIURL, token } from "../env";
import "bootstrap/dist/css/bootstrap.min.css";
import Back1 from "../Images/back.jpg";

// Helper: Format date to dd-mm-yyyy
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // getMonth() is 0-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export default function Event() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const processedEvents = useMemo(() => {
    return events.map((evnt) => ({
      ...evnt,
      imageUrl: evnt.EvtImages?.[0] || Back1,
      displayTitle: evnt.EvtTitle || "Untitled Event",
      displayDesc: evnt.EvtDesc || "No description available",
      formattedFromDate: formatDate(evnt.EvtFromDate),
      formattedToDate: formatDate(evnt.EvtToDate),
    }));
  }, [events]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${BASEAPIURL}/EventList?Token=${encodeURIComponent(token)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      if (result.status && Array.isArray(result.data)) {
        setEvents(result.data);
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchEvents();
  }, [fetchEvents]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleEventClick = useCallback(
    (event) => {
      navigate("/EventDetail", { state: event });
    },
    [navigate]
  );

  const scrollLeft = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  }, []);

  const eventCards = useMemo(() => {
    return processedEvents.map((evnt, index) => (
      <div
        key={evnt.id || index}
        className="col-lg-4 col-md-6 col-sm-12 flex-shrink-0"
        style={{
          minWidth: "300px",
          maxWidth: "300px",
          cursor: "pointer",
        }}
      >
        <div className="main-box-container">
          <div className="box-container">
            <img
              src={evnt.imageUrl}
              alt={evnt.displayTitle}
              className="event-img cover"
              style={{ width: "100%", objectFit: "cover" }}
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = Back1;
              }}
            />
            <div style={{ fontSize: "1.5em", fontWeight: 600, color: "#fff" }}>
              {evnt.displayTitle}
            </div>
            <div className="d-flex justify-content-between text-white px-2">
              <span>{evnt.formattedFromDate}</span>
              <span>{evnt.formattedToDate}</span>
            </div>
            <p className="text-white p-2" style={{ fontSize: "0.95em" }}>
              {evnt.displayDesc}
            </p>
            <h6
              className="text-center mb-4 text-dark"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(evnt);
              }}
              style={{ cursor: "pointer" }}
            >
              View Detail
            </h6>
          </div>
        </div>
      </div>
    ));
  }, [processedEvents, handleEventClick]);

  return (
    <div style={{ marginTop: "4px" }}>
      <div
        className="text-center"
        style={{ fontSize: "3em", fontWeight: 600, color: "#DC143C" }}
      >
        Events
      </div>

      {loading && (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading events...</span>
          </div>
          <p>Loading events...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-danger text-center mx-4" role="alert">
          Error loading events: {error}
        </div>
      )}

      {!loading && !error && (
        <div className="position-relative container mb-5">
          {processedEvents.length > 3 && (
            <>
              <Button
                variant="light"
                className="position-absolute top-50 start-0 translate-middle-y"
                onClick={scrollLeft}
                style={{ zIndex: 110 }}
                aria-label="Scroll left"
              >
                &laquo;
              </Button>
              <Button
                variant="light"
                className="position-absolute top-50 end-0 translate-middle-y"
                onClick={scrollRight}
                style={{ zIndex: 110 }}
                aria-label="Scroll right"
              >
                &raquo;
              </Button>
            </>
          )}

          <div
            ref={scrollRef}
            style={{
              display: "flex",
              overflowX: "auto",
              gap: "1rem",
              paddingBottom: "1rem",
              scrollBehavior: "smooth",
              scrollbarWidth: "none",
            }}
            className="no-scrollbar"
          >
            {eventCards}
          </div>
        </div>
      )}

      <EventTabs />

      <div
        className="scroll-to-top-button rounded-2"
        onClick={scrollToTop}
        style={{
          width: 40,
          height: 40,
          backgroundColor: "#ff68f0",
          color: "#fff",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          cursor: "pointer",
        }}
        aria-label="Scroll to top"
      >
        <IoArrowUpCircleOutline style={{ fontSize: 30 }} />
      </div>
    </div>
  );
}